import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  getCapacityUnitsName,
  showLocationCapacity,
  stationConfigurations,
} from '../../util/stations';
import { ACTIVE, DEFAULT_ROWS_PER_PAGE, LOST, MISSING, RETIRED } from '../../constants';
import {
  changeTab,
  convertCentsToMoney,
  findAccess,
  getPricingString,
  history,
  openGoogleMapsCoordinates,
  stateFormatter,
  toTitleCase,
  zoneTypeFormatter,
} from '../../utils';
import { IStation, StationConfiguration } from '../../ts/interface/station.interface';
import { IHardware } from '../../ts/interface/hardware.interface';
import { ISystem } from '../../ts/interface/system.interface';
import { IMode } from '../../ts/interface/mode.interface';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import { columnsForHardwareV2 } from '../../tableColumns/stationColumns';
import { retireStation } from '../../api/stations';
import { IRate } from '../../ts/interface/rental.interface';
import { connect } from 'react-redux';
import {
  useLocationDetailRelayQuery,
  mapLocationDetailToIStation,
  mapLocationToLocationStats,
} from '../../hooks/stations/query/V2';
import { IStationDetailsProps } from '../../ts/interface/pages/stations.interfaces';
import { LocationAccess } from '../../ts/enums';
import PageContainer from '../../components/Redesing/page-container';
import PageContent from '../../components/Redesing/page-content';
import { PropertyList } from '../../components/Redesing/property-list';
import { PropertyListItem } from '../../components/Redesing/property-list-item';
import PropertyListItemLink from '../../components/Redesing/property-list-item-link';
import Stack from '@mui/material/Stack';
import CardView from '../../components/Redesing/card-view';
import Divider from '@mui/material/Divider';
import { PaginationTable } from '../../components/Redesing/table/pagination-table';
import EditIcon from '@mui/icons-material/Edit';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Grid from '@mui/material/Unstable_Grid2';
import MovaticCustomModal from '../../components/Modal/MovaticCustomModal';
import { SeverityPill } from '../../components/Redesing/severity-pill';
import IconButtonMenu from '../../components/Redesing/icon-button-menu';
import CustomAsyncToast from '../../components/Redesing/custom-async-toast';
import SaveIcon from '@mui/icons-material/Save';
import RentalsTableCard from '../../components/Redesing/table/rental/rentals-table-card';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { getLocationIconByName } from '../../util/utilsR';
import { IColumn } from '../../ts/interface/components/redesing/table.interface';
import customAsyncToast from '../../components/Redesing/custom-async-toast';
import { updateHardware } from '../../api/hardware';
import { IStatePillsOption } from '../../ts/interface/components/redesing/state-pills.interface';
import useSmallScreen from '../../hooks/useSmallScreen';
import CustomMapComponent from '../../components/Redesing/custom-map-component-for-detail-location';
import { StationMediaManager } from '../../components/StationMedia';
import LocationOperatingHoursCard from './locationOperatingHoursCard';

const StationDetails = ({
  systemLoaded,
  system,
  modeInfo,
  movaticAccess,
  systemAccess,
  partner,
}: IStationDetailsProps) => {
  const stationId = useParams<{ stationId: string }>().stationId;
  const currentTab = useParams<{ currentTab: string }>().currentTab || 'general';
  const [retireModal, setRetireModal] = useState(false);
  const [activeHardware, setActiveHardware] = useState(false);
  const [searchRate, setSearchRate] = React.useState('');
  const [pageRate, setPageRate] = React.useState(0);
  const [rowsPerPageRate, setRowsPerPageRate] = React.useState(DEFAULT_ROWS_PER_PAGE);
  const [searchHardware, setSearchHardware] = React.useState('');
  const [pageHardware, setPageHardware] = React.useState(0);
  const [rowsPerPageHardware, setRowsPerPageHardware] =
    React.useState(DEFAULT_ROWS_PER_PAGE);
  const [currentTabV, setCurrentTabV] = useState('general');
  const [exportRateData, setExportRateData] = useState<boolean>(false);
  const [exportHardwareData, setExportHardwareData] = useState<boolean>(false);
  const isSmallScreen = useSmallScreen();
  const handleTabsChange = useCallback(
    (event: any, value: React.SetStateAction<string>) => {
      changeTab(`${stationId}/${value}`, currentTab, 'locations');
    },
    [stationId, currentTab]
  );

  useEffect(() => {
    if (currentTab !== currentTabV) {
      setCurrentTabV(currentTab);
    }
  }, [currentTab, currentTabV]);

  const {
    data: locationData,
    isLoading: isLoadingStations,
    isFetched,
    refetch: refetchLocation,
  } = useLocationDetailRelayQuery(stationId, {
    enabled: !!stationId && systemLoaded,
  });

  const detailedStation = useMemo(() => {
    if (locationData && isFetched) {
      return mapLocationDetailToIStation(locationData);
    }
    return {} as IStation;
  }, [locationData, isFetched]);

  // Hardware at this location comes from GraphQL
  const hardwareAtLocation = useMemo(() => {
    return detailedStation.hardware || [];
  }, [detailedStation.hardware]);

  const checkForActiveHardware = () => {
    const { ZONE } = StationConfiguration;

    // Use hardware from GraphQL location data
    const bikesAtStation = hardwareAtLocation.filter((item: IHardware) => {
      return (
        item.state === ACTIVE &&
        (system.new_hardware ? item.type_name !== 'base_station' : true)
      );
    });

    if (detailedStation.configuration !== ZONE && bikesAtStation.length > 0) {
      setActiveHardware(true);
    } else {
      setActiveHardware(false);
    }
    setRetireModal(true);
  };

  const rowClick = (type: string, row: IHardware) => {
    if (type === 'rental') {
      history.push(`/rentals/${row.id}`);
    } else if (type === 'hardware') {
      if (system.new_hardware) {
        history.push(`/hardware/${row.id}`);
      } else {
        history.push(`/units/${row.id}`);
      }
    }
  };

  const warningCheck = () => {
    const { stationsTitle } = modeInfo.details;
    if (!detailedStation.module && modeInfo.currentMode === 'bikeroom') {
      return `This ${stationsTitle} is not available to make rentals. Assign a lock to make this ${stationsTitle} available to make rentals`;
    }
  };

  const getAccess = (section: string) => {
    return !findAccess(systemAccess).includes(section) && !movaticAccess;
  };

  const button = () => {
    const { state, id } = detailedStation;
    const bts = [
      {
        startIcon: state === RETIRED ? <LocationOnIcon /> : <EditIcon />,
        label: state === RETIRED ? 'Unretire' : 'Edit',
        onClick: () => history.push(`/locations/${id}/edit`),
        disabled: getAccess('physical'),
      },
    ];
    if (state !== RETIRED) {
      bts.push({
        startIcon: <LocationOffIcon />,
        label: 'Retire',
        onClick: () => checkForActiveHardware(),
        disabled: getAccess('physical'),
      });
    }
    return bts;
  };

  const columnsHardware = useMemo(() => {
    return columnsForHardwareV2(
      isSmallScreen,
      (row: IHardware, item: IStatePillsOption) => {
        customAsyncToast({
          promise: () =>
            updateHardware(
              {
                id: row.old_id,
                state: item.value,
                station:
                  row.state !== LOST &&
                  row.state !== MISSING &&
                  (item.value === LOST || item.value === MISSING)
                    ? null
                    : detailedStation.old_id || null,
              },
              {
                hardware_type: system.hardware_type,
              }
            ),
          loadingMessage: 'Updating...',
          successMessage: () => {
            return 'Hardware updated successfully!';
          },
          errorMessage: 'Failed to update hardware',
        }).then(() => {
          refetchLocation();
        });
      }
    ) as IColumn[];
  }, [system, detailedStation.old_id, refetchLocation, isSmallScreen]);

  const tableContent = () => {
    return (
      <>
        <PaginationTable
          exportData={exportHardwareData}
          onExport={() => setExportHardwareData(false)}
          buttons={[
            {
              label: 'Export',
              onClick: () => setExportHardwareData(true),
              startIcon: <FileDownloadIcon />,
            },
          ]}
          isLoading={isLoadingStations}
          items={hardwareAtLocation || []}
          noDataText={'No hardware at this location'}
          onClickReturnRow
          showFilter={false}
          onRowCLick={(row: IHardware) => rowClick('hardware', row)}
          columns={columnsHardware}
          rowsPerPage={rowsPerPageHardware}
          page={pageHardware}
          onPageChange={(page, pageN: number) => {
            setPageHardware(pageN);
          }}
          onRowsPerPageChange={(rowsPerPage) =>
            setRowsPerPageHardware(Number(rowsPerPage.target.value))
          }
          searchText={searchHardware}
          searchPlaceholder={'Search Hardware'}
          handleSearch={(search) => setSearchHardware(search.target.value)}
        />
      </>
    );
  };

  const tableRateContent = () => {
    const { rates } = detailedStation;

    return (
      <>
        <PaginationTable
          exportData={exportRateData}
          onExport={() => setExportRateData(false)}
          csvFileName={'Location_Rates.csv'}
          buttons={[
            {
              label: 'Export',
              onClick: () => setExportRateData(true),
              startIcon: <FileDownloadIcon />,
            },
          ]}
          items={rates || []}
          noDataText={'No rates for this location'}
          customKey={'gid'}
          onRowCLick={(gid: string) => history.push(`/billing/rates/${gid}`)}
          columns={[
            {
              id: 'name',
              label: 'Name',
              shouldExport: true,
            },
            {
              id: 'rate',
              label: 'Rate',
              shouldExport: true,
              format: (row: IRate) => getPricingString({ ...row, base: row.base_rate }),
            },
          ]}
          rowsPerPage={rowsPerPageRate}
          page={pageRate}
          onPageChange={(page, pageN: number) => setPageRate(pageN)}
          onRowsPerPageChange={(rowsPerPage) =>
            setRowsPerPageRate(Number(rowsPerPage.target.value))
          }
          searchText={searchRate}
          searchPlaceholder={'Search Rates'}
          handleSearch={(search) => setSearchRate(search.target.value)}
        />
      </>
    );
  };

  const getSecondContent = () => {
    return (
      <>
        <CustomMapComponent
          system={system}
          modeInfo={modeInfo}
          currentLocation={detailedStation}
        />
        {currentTab !== 'rentals' && (
          <CardView
            isLoading={isLoadingStations}
            content={tableContent()}
            title={`${modeInfo.details.pluralUnits || 'Units'} at this ${
              modeInfo.details.stationsTitle || 'Location'
            }`}
          />
        )}
        {currentTab === 'general' && system.new_hardware && (
          <CardView
            isLoading={isLoadingStations}
            content={tableRateContent()}
            title={`Rates at this ${modeInfo.details.stationsTitle || 'Location'}`}
          />
        )}
      </>
    );
  };

  const modal = () => {
    const { stationsTitle, unitsTitle } = modeInfo.details;
    return (
      <>
        <MovaticCustomModal
          open={retireModal}
          onClose={() => setRetireModal(!retireModal)}
          title={`Retire ${stationsTitle}?`}
          content={`Are you sure you would like to retire this ${stationsTitle.toLowerCase()}?
          ${
            activeHardware
              ? ` Any ${unitsTitle.toLowerCase()} at this ${stationsTitle.toLowerCase()} will need to be assigned to a new ${stationsTitle.toLowerCase()} in order to be accessible to users again.`
              : ''
          }`}
          action={
            <IconButtonMenu
              buttons={[
                {
                  label: 'Cancel',
                  onClick: () => setRetireModal(!retireModal),
                },
                {
                  label: 'Save',
                  onClick: () => {
                    CustomAsyncToast({
                      promise: () => retireStation(detailedStation.id),
                      successMessage: () => `${stationsTitle} retired successfully`,
                      errorMessage: `Error retiring ${stationsTitle}`,
                      loadingMessage: `Retiring ${stationsTitle}...`,
                    }).then((r) => {
                      if (r) {
                        history.push('/locations');
                      }
                    });
                  },
                  startIcon: <SaveIcon />,
                },
              ]}
            />
          }
          customStyles={undefined}
          useContentDividers={undefined}
        />
      </>
    );
  };

  // Get location stats from GraphQL data
  const locationStats = useMemo(() => {
    return mapLocationToLocationStats(locationData);
  }, [locationData]);

  const LocationStatsContent = () => {
    return (
      <div data-id="station-stats">
        <PropertyListItem divider label="Rentals started">
          {locationStats.total_rentals_started ? (
            <>{locationStats.total_rentals_started}</>
          ) : (
            <SeverityPill color={'info'}>N/A</SeverityPill>
          )}
        </PropertyListItem>
        <PropertyListItem divider label="Rentals ended">
          {locationStats.total_rentals_ended ? (
            <>{locationStats.total_rentals_ended}</>
          ) : (
            <SeverityPill color={'info'}>N/A</SeverityPill>
          )}
        </PropertyListItem>
        <PropertyListItem divider label="Rentals started/User (avg)">
          {locationStats.avg_rental_started_per_user ? (
            <>{Number(locationStats.avg_rental_started_per_user).toFixed(2)}</>
          ) : (
            <SeverityPill color={'info'}>N/A</SeverityPill>
          )}
        </PropertyListItem>
        <PropertyListItem label="Rentals ended/User (avg)">
          {locationStats.avg_rental_ended_per_user ? (
            <>{Number(locationStats.avg_rental_ended_per_user).toFixed(2)}</>
          ) : (
            <SeverityPill color={'info'}>N/A</SeverityPill>
          )}
        </PropertyListItem>
      </div>
    );
  };

  function GeneralInfo() {
    const {
      capacity,
      lat,
      lng,
      state,
      module,
      reservations,
      maxReservations,
      configuration,
      zone_type,
      parking_credit,
      slow_zone_speed,
      radius,
      prevent_ending_rentals,
      prevent_starting_rentals,
      available_hardware,
      private: isPrivate,
    } = detailedStation;
    const { currentMode } = modeInfo;
    const unitsName = getCapacityUnitsName(currentMode);
    const { RACK, ZONE } = stationConfigurations;
    // @ts-ignore
    const { name: statusName, color: statusColor } = stateFormatter(state);
    return (
      <PropertyList>
        {configuration && (
          <PropertyListItem
            divider
            label="Location Type"
            value={
              configuration
                ? configuration === RACK
                  ? 'Station'
                  : toTitleCase(configuration)
                : ''
            }
          />
        )}
        <PropertyListItem
          divider
          label="Access"
          value={isPrivate ? LocationAccess.Private : LocationAccess.Public}
        />
        {configuration === ZONE ? (
          <>
            <Divider />
            <PropertyListItem label="Zone Type" value={zoneTypeFormatter(zone_type)} />
          </>
        ) : null}
        {configuration === ZONE ? null : (
          <PropertyListItemLink
            label="Lat/Long"
            text={`${lat}, ${lng}`}
            onClick={() => openGoogleMapsCoordinates(lat, lng)}
          />
        )}
        {configuration === ZONE ? (
          <>
            <Divider />
            <PropertyListItem
              label="Speed Limit"
              value={
                Number.isNaN(slow_zone_speed) || !slow_zone_speed
                  ? 'None'
                  : `${slow_zone_speed} km/h`
              }
            />
          </>
        ) : null}
        {![ZONE].includes(configuration) ? (
          <>
            <Divider />
            <PropertyListItem
              label="Radius"
              value={!radius ? 'None' : `${radius} ${radius === 1 ? 'Meter' : 'Meters'}`}
            />
          </>
        ) : null}
        <Divider />
        <PropertyListItem label="Status" value={statusName}>
          <SeverityPill color={statusColor}>{statusName}</SeverityPill>
        </PropertyListItem>
        {showLocationCapacity(configuration, currentMode) ? (
          <>
            <Divider />
            <PropertyListItem label={`Number of ${unitsName}`}>
              <SeverityPill
                color={
                  capacity && capacity > 0 ? 'success' : !capacity ? 'success' : 'warning'
                }
              >
                {capacity || 'Unlimited'}
              </SeverityPill>
            </PropertyListItem>
          </>
        ) : null}
        <Divider />
        <PropertyListItem label="Availability" value={available_hardware || 0}>
          <SeverityPill
            color={available_hardware && available_hardware > 0 ? 'success' : 'warning'}
          >
            {available_hardware || 0}
          </SeverityPill>
        </PropertyListItem>
        {currentMode === 'bikeRoom' ? (
          <>
            <Divider />
            <PropertyListItemLink
              to={`/units/locks/${module}`}
              text={module || 'None'}
              label={'Lock'}
            />
          </>
        ) : null}
        {system.reservations && movaticAccess && !partner ? (
          <div>
            <Divider />
            <PropertyListItem
              label="Allow Reservations"
              value={reservations !== null && reservations ? 'Yes' : 'No'}
            />
            {reservations ||
            (reservations === null &&
              system.reservations &&
              movaticAccess &&
              !partner) ? (
              <>
                <Divider />
                <PropertyListItem
                  label="Max Reservations"
                  value={
                    maxReservations ||
                    (system.maxReservations ? system.maxReservations : 'None')
                  }
                />
              </>
            ) : null}
          </div>
        ) : null}
        {parking_credit ? (
          <>
            <Divider />
            <PropertyListItem
              label="Parking Credit"
              value={convertCentsToMoney(parking_credit)}
            />
          </>
        ) : null}
        <Divider />
        {[
          {
            label: 'Prevent ending rentals',
            key: 'preventEndingRentals',
            item: prevent_ending_rentals ? 'Yes' : 'No',
          },
          {
            label: 'Prevent starting rentals',
            key: 'preventStartingRentals',
            item: prevent_starting_rentals ? 'Yes' : 'No',
          },
        ].map(({ item, key, label }) => (
          <PropertyListItem divider key={key} label={label} value={item}>
            <SeverityPill color={item === 'Yes' ? 'warning' : 'success'}>
              {item}
            </SeverityPill>
          </PropertyListItem>
        ))}
      </PropertyList>
    );
  }

  return (
    <PageContainer warningMessage={warningCheck()} isLoading={isLoadingStations}>
      <>
        <PageContent
          backLink={'/locations'}
          chipLabel={modeInfo.details.stationsTitle || 'Location'}
          contentTitle={'Locations'}
          itemName={toTitleCase(detailedStation?.name)}
          itemChipValue={detailedStation?.id}
          detailIcon={
            detailedStation?.configuration ? (
              getLocationIconByName(detailedStation?.configuration).icon
            ) : (
              <LocationOnIcon />
            )
          }
          itemActions={null}
          itemTopButtons={<IconButtonMenu buttons={button()} />}
          handleTabsChange={handleTabsChange}
          currentTabV={currentTabV}
          tabsList={[
            { label: 'Details', value: 'general' },
            { label: 'Media', value: 'media' },
            { label: 'Rentals', value: 'rentals' },
          ]}
          tabsChildren={
            <>
              {currentTabV === 'general' && (
                <Grid container spacing={2}>
                  <Grid xs={12} lg={4}>
                    <Stack spacing={2}>
                      <CardView
                        isLoading={isLoadingStations}
                        content={<GeneralInfo />}
                        title={'Location Details'}
                      />
                      <LocationOperatingHoursCard
                        location={locationData}
                        isLoading={isLoadingStations}
                      />
                      <CardView
                        isLoading={isLoadingStations}
                        content={<LocationStatsContent />}
                        title={'Location Stats'}
                      />
                    </Stack>
                  </Grid>
                  <Grid xs={12} lg={8}>
                    <Stack spacing={2}>{getSecondContent()}</Stack>
                  </Grid>
                </Grid>
              )}
              {currentTab === 'media' && (
                <CardView
                  isLoading={isLoadingStations}
                  content={
                    <Stack p={2}>
                      <StationMediaManager
                        stationId={detailedStation.id}
                        stationName={detailedStation.name}
                        readOnly
                      />
                    </Stack>
                  }
                  title={'Media'}
                />
              )}
              {currentTab === 'rentals' && (
                <>
                  <RentalsTableCard
                    noRentalsText={
                      'This location does not have any rentals matching the criteria'
                    }
                    parameters={{ station_id: detailedStation.old_id }}
                  />
                </>
              )}
            </>
          }
        />
        {modal()}
      </>
    </PageContainer>
  );
};

export default connect(
  (state: {
    admin: {
      systemAccess: number;
      admin: {
        admin: {
          movaticAccess: number;
          partner: number;
        };
      };
    };
    system: {
      current: ISystem;
      isLoaded: boolean;
    };
    mode: IMode;
    module: {
      modules: { mac: string; state: number }[];
    };
  }) => ({
    systemAccess: state.admin.systemAccess,
    system: state.system.current,
    modeInfo: state.mode,
    systemLoaded: state.system.isLoaded,
    partner: state.admin.admin.admin.partner,
    movaticAccess: state.admin.admin.admin.movaticAccess,
  }),
  () => ({})
)(StationDetails);
